import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DollarSign, Clock, AlertTriangle, CheckCircle, Phone, Mail, 
  Calendar, Building2, User, FileText, TrendingUp, AlertCircle,
  Plus, Eye, MoreVertical, Loader2, CreditCard
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '../lib/api';

const ageBracketConfig = {
  current: { color: 'bg-green-100 text-green-800', label: 'Current' },
  '30': { color: 'bg-yellow-100 text-yellow-800', label: '1-30 Days' },
  '60': { color: 'bg-orange-100 text-orange-800', label: '31-60 Days' },
  '90': { color: 'bg-red-100 text-red-800', label: '61-90 Days' },
  over90: { color: 'bg-red-200 text-red-900', label: '90+ Days' }
};

const priorityConfig = {
  low: { color: 'bg-gray-100 text-gray-800', label: 'Low' },
  medium: { color: 'bg-blue-100 text-blue-800', label: 'Medium' },
  high: { color: 'bg-orange-100 text-orange-800', label: 'High' },
  urgent: { color: 'bg-red-100 text-red-800', label: 'Urgent' }
};

const outcomeConfig = {
  pending: { color: 'bg-gray-100 text-gray-800', label: 'Pending' },
  contacted: { color: 'bg-blue-100 text-blue-800', label: 'Contacted' },
  promised: { color: 'bg-yellow-100 text-yellow-800', label: 'Promised' },
  escalated: { color: 'bg-orange-100 text-orange-800', label: 'Escalated' },
  resolved: { color: 'bg-green-100 text-green-800', label: 'Resolved' }
};

export const CollectionsPage = () => {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [payments, setPayments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [ageFilter, setAgeFilter] = useState('all');
  
  // Dialogs
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [followUpDialogOpen, setFollowUpDialogOpen] = useState(false);
  const [outcomeDialogOpen, setOutcomeDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedFollowUp, setSelectedFollowUp] = useState(null);
  
  // Forms
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'bank_transfer',
    reference_number: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  const [followUpForm, setFollowUpForm] = useState({
    follow_up_type: 'payment_reminder',
    due_date: new Date().toISOString().split('T')[0],
    priority: 'medium',
    contact_method: 'phone',
    contact_person: '',
    notes: ''
  });
  
  const [outcomeForm, setOutcomeForm] = useState({
    outcome: '',
    outcome_notes: '',
    promised_date: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedCompany, ageFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCompany !== 'all') params.company_id = selectedCompany;
      if (ageFilter !== 'all') params.age_bracket = ageFilter;
      
      const [statsRes, invoicesRes, followUpsRes, paymentsRes, companiesRes] = await Promise.all([
        api.get('/collections/stats/summary', { params: selectedCompany !== 'all' ? { company_id: selectedCompany } : {} }),
        api.get('/collections/outstanding-invoices', { params }),
        api.get('/collections/follow-ups', { params: selectedCompany !== 'all' ? { company_id: selectedCompany } : {} }),
        api.get('/collections/payments', { params: selectedCompany !== 'all' ? { company_id: selectedCompany } : {} }),
        api.get('/companies')
      ]);
      
      setStats(statsRes.data);
      setInvoices(invoicesRes.data);
      setFollowUps(followUpsRes.data);
      setPayments(paymentsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load collections data');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    
    try {
      const res = await api.post(`/collections/record-payment/${selectedInvoice.id}`, paymentForm);
      toast.success(`Payment recorded: ${res.data.amount} ${selectedInvoice.currency}`);
      setPaymentDialogOpen(false);
      resetPaymentForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const handleCreateFollowUp = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    
    try {
      await api.post('/collections/follow-ups', {
        ...followUpForm,
        company_id: selectedInvoice.company_id,
        invoice_id: selectedInvoice.id,
        client_id: selectedInvoice.client_id,
        client_name: selectedInvoice.client_name
      });
      toast.success('Follow-up created');
      setFollowUpDialogOpen(false);
      resetFollowUpForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create follow-up');
    }
  };

  const handleUpdateOutcome = async (e) => {
    e.preventDefault();
    if (!selectedFollowUp) return;
    
    try {
      await api.patch(`/collections/follow-ups/${selectedFollowUp.id}/update-outcome`, outcomeForm);
      toast.success('Follow-up updated');
      setOutcomeDialogOpen(false);
      setOutcomeForm({ outcome: '', outcome_notes: '', promised_date: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update follow-up');
    }
  };

  const openPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      ...paymentForm,
      amount: invoice.amount_outstanding
    });
    setPaymentDialogOpen(true);
  };

  const openFollowUpDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setFollowUpForm({
      ...followUpForm,
      contact_person: invoice.client_name
    });
    setFollowUpDialogOpen(true);
  };

  const openOutcomeDialog = (followUp) => {
    setSelectedFollowUp(followUp);
    setOutcomeDialogOpen(true);
  };

  const resetPaymentForm = () => {
    setPaymentForm({
      amount: '',
      payment_method: 'bank_transfer',
      reference_number: '',
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setSelectedInvoice(null);
  };

  const resetFollowUpForm = () => {
    setFollowUpForm({
      follow_up_type: 'payment_reminder',
      due_date: new Date().toISOString().split('T')[0],
      priority: 'medium',
      contact_method: 'phone',
      contact_person: '',
      notes: ''
    });
    setSelectedInvoice(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="collections-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Collections & Payments</h1>
          <p className="text-muted-foreground">Track outstanding invoices, payments, and follow-ups</p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedCompany} onValueChange={setSelectedCompany}>
            <SelectTrigger className="w-48" data-testid="company-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Companies" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Companies</SelectItem>
              {companies.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="p-4 border-l-4 border-l-green-500">
            <div className="text-sm text-muted-foreground">Current</div>
            <div className="text-xl font-bold text-green-600">SAR {stats.aging.current.total.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stats.aging.current.count} invoices</div>
          </Card>
          <Card className="p-4 border-l-4 border-l-yellow-500">
            <div className="text-sm text-muted-foreground">1-30 Days</div>
            <div className="text-xl font-bold text-yellow-600">SAR {stats.aging['30'].total.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stats.aging['30'].count} invoices</div>
          </Card>
          <Card className="p-4 border-l-4 border-l-orange-500">
            <div className="text-sm text-muted-foreground">31-60 Days</div>
            <div className="text-xl font-bold text-orange-600">SAR {stats.aging['60'].total.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stats.aging['60'].count} invoices</div>
          </Card>
          <Card className="p-4 border-l-4 border-l-red-500">
            <div className="text-sm text-muted-foreground">61-90 Days</div>
            <div className="text-xl font-bold text-red-600">SAR {stats.aging['90'].total.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stats.aging['90'].count} invoices</div>
          </Card>
          <Card className="p-4 border-l-4 border-l-red-800">
            <div className="text-sm text-muted-foreground">90+ Days</div>
            <div className="text-xl font-bold text-red-800">SAR {stats.aging.over90.total.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stats.aging.over90.count} invoices</div>
          </Card>
          <Card className="p-4 border-l-4 border-l-blue-500">
            <div className="text-sm text-muted-foreground">Total Outstanding</div>
            <div className="text-xl font-bold">SAR {stats.aging.total.total.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">{stats.aging.total.count} invoices</div>
          </Card>
        </div>
      )}

      {/* Secondary Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <div>
                <div className="text-sm text-muted-foreground">Collected (30 days)</div>
                <div className="text-lg font-bold">SAR {stats.collections.total_collected_30_days.toLocaleString()}</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              <div>
                <div className="text-sm text-muted-foreground">Pending Follow-ups</div>
                <div className="text-lg font-bold">{stats.follow_ups.pending}</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <div className="text-sm text-muted-foreground">Overdue Follow-ups</div>
                <div className="text-lg font-bold">{stats.follow_ups.overdue}</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <div className="text-sm text-muted-foreground">High Risk Amount</div>
                <div className="text-lg font-bold">SAR {stats.risk_summary.high_risk_amount.toLocaleString()}</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="outstanding" className="space-y-4">
        <TabsList>
          <TabsTrigger value="outstanding">Outstanding Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="followups">Follow-ups ({followUps.length})</TabsTrigger>
          <TabsTrigger value="payments">Recent Payments ({payments.length})</TabsTrigger>
        </TabsList>

        {/* Outstanding Invoices Tab */}
        <TabsContent value="outstanding">
          <Card>
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Outstanding Invoices</h3>
                <Select value={ageFilter} onValueChange={setAgeFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Ages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ages</SelectItem>
                    <SelectItem value="current">Current</SelectItem>
                    <SelectItem value="30">1-30 Days</SelectItem>
                    <SelectItem value="60">31-60 Days</SelectItem>
                    <SelectItem value="90">61-90 Days</SelectItem>
                    <SelectItem value="over90">90+ Days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {invoices.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No outstanding invoices</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="pb-2">Invoice #</th>
                      <th className="pb-2">Client</th>
                      <th className="pb-2">Total</th>
                      <th className="pb-2">Outstanding</th>
                      <th className="pb-2">Age</th>
                      <th className="pb-2">Due Date</th>
                      <th className="pb-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id} className="border-b hover:bg-muted/50" data-testid={`invoice-${inv.id}`}>
                        <td className="py-3 font-medium">{inv.invoice_number}</td>
                        <td className="py-3">{inv.client_name}</td>
                        <td className="py-3">{inv.currency || 'SAR'} {inv.total?.toLocaleString()}</td>
                        <td className="py-3 font-semibold text-red-600">
                          {inv.currency || 'SAR'} {inv.amount_outstanding?.toLocaleString()}
                        </td>
                        <td className="py-3">
                          <Badge className={ageBracketConfig[inv.age_bracket]?.color}>
                            {inv.days_overdue > 0 ? `${inv.days_overdue}d overdue` : 'Current'}
                          </Badge>
                        </td>
                        <td className="py-3 text-sm">{inv.due_date?.split('T')[0]}</td>
                        <td className="py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openPaymentDialog(inv)}>
                                <DollarSign className="h-4 w-4 mr-2" />
                                Record Payment
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openFollowUpDialog(inv)}>
                                <Phone className="h-4 w-4 mr-2" />
                                Create Follow-up
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Follow-ups Tab */}
        <TabsContent value="followups">
          <Card>
            <div className="p-4">
              <h3 className="font-semibold mb-4">Payment Follow-ups</h3>
              {followUps.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No follow-ups</p>
              ) : (
                <div className="space-y-3">
                  {followUps.map(fu => (
                    <div key={fu.id} className="border rounded-lg p-4 hover:bg-muted/50" data-testid={`followup-${fu.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-semibold">{fu.client_name}</span>
                            <Badge className={priorityConfig[fu.priority]?.color}>
                              {priorityConfig[fu.priority]?.label}
                            </Badge>
                            <Badge className={outcomeConfig[fu.outcome || 'pending']?.color}>
                              {outcomeConfig[fu.outcome || 'pending']?.label}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              <span>Due: {fu.due_date}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              {fu.contact_method === 'phone' ? <Phone className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                              <span className="capitalize">{fu.contact_method}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Type: </span>
                              <span className="capitalize">{fu.follow_up_type?.replace('_', ' ')}</span>
                            </div>
                          </div>
                          {fu.notes && (
                            <p className="mt-2 text-sm text-muted-foreground">{fu.notes}</p>
                          )}
                        </div>
                        {fu.status === 'pending' && (
                          <Button size="sm" onClick={() => openOutcomeDialog(fu)}>
                            Update Outcome
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card>
            <div className="p-4">
              <h3 className="font-semibold mb-4">Recent Payments</h3>
              {payments.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">No payments recorded</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left text-sm text-muted-foreground">
                      <th className="pb-2">Date</th>
                      <th className="pb-2">Client</th>
                      <th className="pb-2">Amount</th>
                      <th className="pb-2">Method</th>
                      <th className="pb-2">Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map(pmt => (
                      <tr key={pmt.id} className="border-b hover:bg-muted/50">
                        <td className="py-3">{pmt.payment_date}</td>
                        <td className="py-3">{pmt.client_name}</td>
                        <td className="py-3 font-semibold text-green-600">
                          {pmt.currency || 'SAR'} {pmt.amount?.toLocaleString()}
                        </td>
                        <td className="py-3 capitalize">{pmt.payment_method?.replace('_', ' ')}</td>
                        <td className="py-3 text-sm text-muted-foreground">{pmt.reference_number || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4">
            {selectedInvoice && (
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-sm">
                  <span className="text-muted-foreground">Invoice:</span>{' '}
                  <span className="font-medium">{selectedInvoice.invoice_number}</span>
                </p>
                <p className="text-sm">
                  <span className="text-muted-foreground">Outstanding:</span>{' '}
                  <span className="font-medium text-red-600">
                    {selectedInvoice.currency || 'SAR'} {selectedInvoice.amount_outstanding?.toLocaleString()}
                  </span>
                </p>
              </div>
            )}
            
            <div>
              <Label>Payment Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                required
                data-testid="payment-amount-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Payment Method</Label>
                <Select 
                  value={paymentForm.payment_method} 
                  onValueChange={(v) => setPaymentForm({...paymentForm, payment_method: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm({...paymentForm, payment_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Reference Number</Label>
              <Input
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm({...paymentForm, reference_number: e.target.value})}
                placeholder="Bank reference or check number"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-payment-btn">
                <CreditCard className="h-4 w-4 mr-2" />
                Record Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Follow-up Dialog */}
      <Dialog open={followUpDialogOpen} onOpenChange={setFollowUpDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Follow-up</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateFollowUp} className="space-y-4">
            {selectedInvoice && (
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-sm">
                  <span className="text-muted-foreground">Client:</span>{' '}
                  <span className="font-medium">{selectedInvoice.client_name}</span>
                </p>
                <p className="text-sm">
                  <span className="text-muted-foreground">Outstanding:</span>{' '}
                  <span className="font-medium text-red-600">
                    {selectedInvoice.currency || 'SAR'} {selectedInvoice.amount_outstanding?.toLocaleString()}
                  </span>
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Follow-up Type</Label>
                <Select 
                  value={followUpForm.follow_up_type} 
                  onValueChange={(v) => setFollowUpForm({...followUpForm, follow_up_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payment_reminder">Payment Reminder</SelectItem>
                    <SelectItem value="escalation">Escalation</SelectItem>
                    <SelectItem value="final_notice">Final Notice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select 
                  value={followUpForm.priority} 
                  onValueChange={(v) => setFollowUpForm({...followUpForm, priority: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contact Method</Label>
                <Select 
                  value={followUpForm.contact_method} 
                  onValueChange={(v) => setFollowUpForm({...followUpForm, contact_method: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="phone">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="visit">In-Person Visit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={followUpForm.due_date}
                  onChange={(e) => setFollowUpForm({...followUpForm, due_date: e.target.value})}
                />
              </div>
            </div>
            
            <div>
              <Label>Contact Person</Label>
              <Input
                value={followUpForm.contact_person}
                onChange={(e) => setFollowUpForm({...followUpForm, contact_person: e.target.value})}
                placeholder="Person to contact"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={followUpForm.notes}
                onChange={(e) => setFollowUpForm({...followUpForm, notes: e.target.value})}
                placeholder="Follow-up notes..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFollowUpDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-followup-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Follow-up
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Update Outcome Dialog */}
      <Dialog open={outcomeDialogOpen} onOpenChange={setOutcomeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Follow-up Outcome</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateOutcome} className="space-y-4">
            <div>
              <Label>Outcome *</Label>
              <Select 
                value={outcomeForm.outcome} 
                onValueChange={(v) => setOutcomeForm({...outcomeForm, outcome: v})}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contacted">Contacted - No commitment</SelectItem>
                  <SelectItem value="promised">Client Promised Payment</SelectItem>
                  <SelectItem value="escalated">Escalated to Management</SelectItem>
                  <SelectItem value="resolved">Resolved - Payment Received</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {outcomeForm.outcome === 'promised' && (
              <div>
                <Label>Promised Payment Date</Label>
                <Input
                  type="date"
                  value={outcomeForm.promised_date}
                  onChange={(e) => setOutcomeForm({...outcomeForm, promised_date: e.target.value})}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  A new follow-up will be created for this date
                </p>
              </div>
            )}
            
            <div>
              <Label>Notes</Label>
              <Textarea
                value={outcomeForm.outcome_notes}
                onChange={(e) => setOutcomeForm({...outcomeForm, outcome_notes: e.target.value})}
                placeholder="Outcome details..."
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOutcomeDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Update Outcome</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectionsPage;
